<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useAccount } from 'dashboard/composables/useAccount';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import {
  getBalance,
  updateVoiceSettings,
} from 'dashboard/api/relayVoiceManager';
import { formatMoney, isLowBalance } from './helpers';

const { t } = useI18n();
const { accountId } = useAccount();

const loading = ref(true);
const saving = ref(false);
const balance = ref(null);
const state = ref('loading');
const threshold = ref('');

const low = computed(() =>
  isLowBalance(balance.value?.amount, balance.value?.low_balance_threshold)
);
const checkedAt = computed(() => {
  if (!balance.value?.checkedAt) return '';
  return new Date(balance.value.checkedAt).toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
  });
});

const load = async () => {
  loading.value = true;
  try {
    balance.value = await getBalance(accountId.value);
    state.value = 'ok';
  } catch (error) {
    const body = error.response?.data || {};
    balance.value = body;
    state.value =
      body.status === 'provider_not_connected' ? 'not_connected' : 'error';
  } finally {
    threshold.value = balance.value?.low_balance_threshold
      ? String(Number(balance.value.low_balance_threshold))
      : '';
    loading.value = false;
  }
};

const saveThreshold = async () => {
  saving.value = true;
  try {
    const result = await updateVoiceSettings(accountId.value, {
      low_balance_threshold: threshold.value === '' ? null : threshold.value,
    });
    balance.value = {
      ...balance.value,
      low_balance_threshold: result.low_balance_threshold,
    };
    useAlert(t('TEAM_LEAD.BALANCE.THRESHOLD_SAVED'));
  } catch (error) {
    useAlert(error.response?.data?.error || t('TEAM_LEAD.GENERIC_ERROR'));
  } finally {
    saving.value = false;
  }
};

onMounted(load);
defineExpose({ load, low });
</script>

<template>
  <section
    data-tour="balance"
    class="rounded-xl outline outline-1 outline-n-container bg-n-solid-1 p-5 flex flex-col gap-5"
    :aria-busy="loading"
  >
    <header class="flex items-start justify-between gap-4">
      <div class="flex flex-col gap-1">
        <h2 class="text-base font-semibold text-n-slate-12">
          {{ t('TEAM_LEAD.BALANCE.TITLE') }}
        </h2>
        <p class="text-sm text-n-slate-11">
          {{ t('TEAM_LEAD.BALANCE.DESCRIPTION') }}
        </p>
      </div>
      <Button
        icon="i-lucide-refresh-cw"
        variant="ghost"
        color="slate"
        size="sm"
        :is-loading="loading"
        :aria-label="t('TEAM_LEAD.BALANCE.REFRESH')"
        @click="load"
      />
    </header>

    <div
      v-if="low"
      role="alert"
      data-test-id="low-balance"
      class="flex items-start gap-3 rounded-lg bg-n-ruby-3 px-4 py-3 text-sm text-n-ruby-11"
    >
      <span class="i-lucide-triangle-alert size-4 mt-0.5 shrink-0" />
      <span>
        {{
          t('TEAM_LEAD.BALANCE.LOW', {
            threshold: formatMoney(
              balance.low_balance_threshold,
              balance.currency
            ),
          })
        }}
      </span>
    </div>

    <div v-if="state === 'ok'" class="flex items-end gap-3 flex-wrap">
      <span
        class="text-3xl font-semibold tabular-nums tracking-tight"
        :class="low ? 'text-n-ruby-11' : 'text-n-slate-12'"
        data-test-id="balance-amount"
      >
        {{ formatMoney(balance.amount, balance.currency) }}
      </span>
      <span class="text-sm text-n-slate-10 pb-1">
        {{ t('TEAM_LEAD.BALANCE.CHECKED_AT', { time: checkedAt }) }}
      </span>
    </div>
    <p
      v-else-if="state === 'not_connected'"
      class="text-sm text-n-slate-11"
      data-test-id="balance-not-connected"
    >
      {{ t('TEAM_LEAD.BALANCE.NOT_CONNECTED') }}
    </p>
    <p v-else-if="state === 'error'" class="text-sm text-n-amber-11">
      {{ balance?.error || t('TEAM_LEAD.BALANCE.UNAVAILABLE') }}
    </p>

    <form
      class="flex items-end gap-3 flex-wrap border-t border-n-weak pt-4"
      data-tour="threshold"
      @submit.prevent="saveThreshold"
    >
      <div class="w-56">
        <Input
          v-model="threshold"
          type="number"
          min="0"
          :label="t('TEAM_LEAD.BALANCE.THRESHOLD')"
          :placeholder="t('TEAM_LEAD.BALANCE.THRESHOLD_PLACEHOLDER')"
        />
      </div>
      <Button
        type="submit"
        :label="t('TEAM_LEAD.SAVE')"
        size="sm"
        variant="faded"
        :is-loading="saving"
      />
      <p class="text-xs text-n-slate-10 basis-full">
        {{ t('TEAM_LEAD.BALANCE.THRESHOLD_HINT') }}
      </p>
    </form>
  </section>
</template>
